import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ShieldAlert, RotateCcw, Play, Zap, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const DataShieldGame = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [gameState, setGameState] = useState("start"); // start, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const requestRef = useRef();
  
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Game State Refs
  const mousePos = useRef({ x: 0, y: 0 });
  const shieldAngle = useRef(0);
  const enemies = useRef([]);
  const particles = useRef([]);
  const coreHealth = useRef(100);
  const frameCount = useRef(0);

  // Constants
  const CORE_RADIUS = 30;
  const SHIELD_RADIUS = 80;
  const SHIELD_WIDTH = 60; // in degrees
  const ENEMY_SPEED = 2.5;
  const SPAWN_RATE = 60; // frames

  useEffect(() => {
    const saved = localStorage.getItem("meraki_datashield_high_score");
    if (saved) setHighScore(parseInt(saved));

    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
    };

    const updateCanvasSize = () => {
      if (containerRef.current && canvasRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        canvasRef.current.width = clientWidth * window.devicePixelRatio;
        canvasRef.current.height = clientHeight * window.devicePixelRatio;
      }
    };

    // Force non-passive touchmove listener to prevent scrolling
    const container = containerRef.current;
    const preventDefault = (e) => {
      if (container && container.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (container) {
      container.addEventListener('touchmove', preventDefault, { passive: false });
    }

    checkTouch();
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (container) {
        container.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  const initGame = () => {
    enemies.current = [];
    particles.current = [];
    coreHealth.current = 100;
    setScore(0);
    setGameState("playing");
    frameCount.current = 0;
  };

  const createParticles = (x, y, color, count = 10) => {
    for (let i = 0; i < count; i++) {
      particles.current.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 8,
        vy: (Math.random() - 0.5) * 8,
        size: Math.random() * 3 + 1,
        life: 1.0,
        color
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!containerRef.current || isTouchDevice) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    mousePos.current = { x, y };
    shieldAngle.current = Math.atan2(y, x);
  };

  const handleTouchMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left - rect.width / 2;
    const y = touch.clientY - rect.top - rect.height / 2;
    mousePos.current = { x, y };
    shieldAngle.current = Math.atan2(y, x);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const update = () => {
      if (gameState !== "playing") return;

      frameCount.current++;

      // Spawn Enemies
      if (frameCount.current % Math.max(25, SPAWN_RATE - Math.floor(score / 5)) === 0) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.max(canvas.width, canvas.height) / 1.2;
        enemies.current.push({
          x: Math.cos(angle) * dist + canvas.width / 2,
          y: Math.sin(angle) * dist + canvas.height / 2,
          angle: angle + Math.PI, // point towards center
          speed: ENEMY_SPEED + (score * 0.04),
          type: Math.random() > 0.15 ? "tracker" : "powerup"
        });
      }

      // Update Enemies
      enemies.current.forEach((enemy, index) => {
        const dx = canvas.width / 2 - enemy.x;
        const dy = canvas.height / 2 - enemy.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        enemy.x += (dx / dist) * enemy.speed;
        enemy.y += (dy / dist) * enemy.speed;

        // Collision with Shield
        const enemyAngle = Math.atan2(dy, dx) + Math.PI; // angle from center to enemy
        const normalizedEnemyAngle = ((enemyAngle % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const normalizedShieldAngle = ((shieldAngle.current % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        
        const angleDiff = Math.abs(normalizedEnemyAngle - normalizedShieldAngle);
        const wrappedDiff = Math.min(angleDiff, Math.PI * 2 - angleDiff);

        if (dist <= SHIELD_RADIUS + 15 && dist >= SHIELD_RADIUS - 15) {
          if (wrappedDiff < (SHIELD_WIDTH * Math.PI / 360)) {
            // Blocked!
            if (enemy.type === "tracker") {
              setScore(s => s + 1);
              createParticles(enemy.x, enemy.y, "#c9a961");
            } else {
              setScore(s => s + 5);
              createParticles(enemy.x, enemy.y, "#4ade80", 20);
            }
            enemies.current.splice(index, 1);
            return;
          }
        }

        // Collision with Core
        if (dist < CORE_RADIUS) {
          if (enemy.type === "tracker") {
            coreHealth.current -= 20;
            createParticles(enemy.x, enemy.y, "#ef4444", 20);
            if (coreHealth.current <= 0) {
              setGameState("gameOver");
            }
          } else {
            // Missed powerup
          }
          enemies.current.splice(index, 1);
        }
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;

      // Draw Grid
      ctx.strokeStyle = "rgba(201, 169, 97, 0.05)";
      ctx.lineWidth = 1;
      for (let i = 0; i < canvas.width; i += 50) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, canvas.height); ctx.stroke();
      }
      for (let i = 0; i < canvas.height; i += 50) {
        ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(canvas.width, i); ctx.stroke();
      }

      // Draw Core
      const pulse = 1 + Math.sin(Date.now() / 200) * 0.1;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, CORE_RADIUS * pulse);
      grad.addColorStop(0, "#c9a961");
      grad.addColorStop(1, "transparent");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, CORE_RADIUS * pulse, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = "#c9a961";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, CORE_RADIUS, 0, Math.PI * 2);
      ctx.stroke();

      // Draw Shield
      if (gameState === "playing") {
        ctx.strokeStyle = "#c9a961";
        ctx.lineWidth = 6;
        ctx.lineCap = "round";
        ctx.beginPath();
        const start = shieldAngle.current - (SHIELD_WIDTH * Math.PI / 360);
        const end = shieldAngle.current + (SHIELD_WIDTH * Math.PI / 360);
        ctx.arc(cx, cy, SHIELD_RADIUS, start, end);
        ctx.stroke();

        ctx.strokeStyle = "rgba(201, 169, 97, 0.3)";
        ctx.lineWidth = 12;
        ctx.beginPath();
        ctx.arc(cx, cy, SHIELD_RADIUS, start, end);
        ctx.stroke();
      }

      // Draw Enemies
      enemies.current.forEach(enemy => {
        ctx.save();
        ctx.translate(enemy.x, enemy.y);
        ctx.rotate(Math.atan2(cy - enemy.y, cx - enemy.x));

        if (enemy.type === "tracker") {
          ctx.fillStyle = "#ef4444";
          ctx.beginPath();
          ctx.moveTo(10, 0);
          ctx.lineTo(-5, -7);
          ctx.lineTo(-5, 7);
          ctx.closePath();
          ctx.fill();
          ctx.shadowBlur = 10;
          ctx.shadowColor = "#ef4444";
          ctx.fill();
        } else {
          ctx.fillStyle = "#4ade80";
          ctx.beginPath();
          ctx.arc(0, 0, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 15;
          ctx.shadowColor = "#4ade80";
          ctx.fill();
        }
        ctx.restore();
      });

      // Draw Particles
      particles.current.forEach((p, i) => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.02;
      });
      particles.current = particles.current.filter(p => p.life > 0);
      ctx.globalAlpha = 1.0;

      if (gameState === "playing") {
        requestRef.current = requestAnimationFrame(() => {
          update();
          draw();
        });
      } else if (gameState === "gameOver" || gameState === "start") {
        requestRef.current = requestAnimationFrame(draw);
      }
    };

    requestRef.current = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, score, isTouchDevice]);

  useEffect(() => {
    if (gameState === "gameOver" && score > highScore) {
      setHighScore(score);
      localStorage.setItem("meraki_datashield_high_score", score.toString());
    }
  }, [gameState, score, highScore]);

  return (
    <section className="py-24 relative overflow-hidden bg-[#050505]">
      <div className="container-xl px-4 md:px-6">
        <div className="flex flex-col mb-12">
          <div className="text-center md:text-left mb-8">
            <Kicker index="SEC-01" label="Interactive Experience" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Privacy <span className="italic gold-text">Guardian</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-[10px] md:text-sm uppercase tracking-[0.2em] max-w-lg mx-auto leading-relaxed">
              Protect your data core from incoming trackers. Rotate your shield to deflect threats.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div 
          ref={containerRef}
          className="relative max-w-4xl mx-auto aspect-[3/4] md:aspect-[16/9] rounded-[32px] md:rounded-[40px] overflow-hidden border border-[rgba(201,169,97,0.15)] bg-black shadow-[0_0_80px_rgba(0,0,0,0.8)] group cursor-none touch-none"
          data-lenis-prevent
          onMouseMove={handleMouseMove}
          onTouchMove={(e) => { e.stopPropagation(); handleTouchMove(e); }}
          onWheel={(e) => e.stopPropagation()}
          onClick={() => gameState === "start" && initGame()}
        >
          <canvas 
            ref={canvasRef}
            width={1200}
            height={675}
            className="w-full h-full object-cover"
          />

          {/* Custom Cursor for Game - Only on desktop */}
          {gameState === "playing" && !isTouchDevice && (
            <div 
              className="absolute pointer-events-none w-8 h-8 border border-[var(--gold)] rounded-full -translate-x-1/2 -translate-y-1/2 z-50 transition-transform duration-100"
              style={{ 
                left: mousePos.current.x + (containerRef.current?.offsetWidth || 0) / 2, 
                top: mousePos.current.y + (containerRef.current?.offsetHeight || 0) / 2
              }}
            >
              <div className="absolute inset-0 bg-[var(--gold)] opacity-20 rounded-full animate-ping" />
            </div>
          )}

          <AnimatePresence>
            {gameState === "start" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-30"
              >
                <div 
                  className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: "url('/images/privacy_guardian_thumb.png')" }}
                />
                <div className="absolute inset-0 z-10 bg-black/80 backdrop-blur-[6px]" />

                <div className="relative z-20 flex flex-col items-center p-8 md:p-12 rounded-3xl bg-black/40 backdrop-blur-xl max-w-[85%] md:max-w-none">
                  <Shield className="text-[var(--gold)] w-12 h-12 md:w-16 md:h-16 mb-6 md:mb-8 animate-pulse" />
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-4 italic text-center leading-tight">Initialize Security?</h3>
                  <p className="text-[var(--cream-muted)] text-[8px] md:text-[10px] tracking-[0.3em] uppercase mb-8 md:mb-10 text-center leading-relaxed">
                    Protect the core. <br /> Rotate your shield with {isTouchDevice ? "touch" : "mouse"}.
                  </p>
                  <button 
                    className="btn-primary px-8 md:px-10 py-4" 
                    onClick={(e) => { e.stopPropagation(); initGame(); }}
                  >
                    Deploy Shield
                  </button>
                </div>
              </motion.div>
            )}

            {gameState === "gameOver" && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/95 backdrop-blur-2xl z-30 p-6 text-center"
              >
                <ShieldAlert className="text-red-500 w-12 h-12 md:w-20 md:h-20 mb-4 md:mb-6" />
                <h3 className="font-display text-3xl md:text-5xl text-white mb-2 tracking-tighter leading-tight uppercase">DATA BREACH</h3>
                <p className="text-red-400 font-impact text-[10px] md:text-sm tracking-[0.4em] uppercase mb-8 md:mb-12">Core Integrity Compromised</p>
                
                <div className="flex gap-8 md:gap-16 mb-10 md:mb-12">
                  <div className="text-center">
                    <div className="text-[8px] md:text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">THREATS DEFLECTED</div>
                    <div className="text-3xl md:text-5xl font-display text-white">{score}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[8px] md:text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">RECORD</div>
                    <div className="text-3xl md:text-5xl font-display text-[var(--gold)]">{highScore}</div>
                  </div>
                </div>

                <button 
                  className="btn-primary px-8 md:px-12 py-4 md:py-5" 
                  onClick={(e) => { e.stopPropagation(); initGame(); }}
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Reboot System
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* HUD */}
          {gameState === "playing" && (
            <>
              <div className="absolute top-10 left-10 z-20 pointer-events-none">
                <div className="text-[10px] text-[var(--gold)] uppercase tracking-[0.3em] mb-1">INTEGRITY</div>
                <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-red-500 to-[var(--gold)]"
                    initial={{ width: "100%" }}
                    animate={{ width: `${coreHealth.current}%` }}
                  />
                </div>
              </div>
              <div className="absolute top-10 right-10 text-right z-20 pointer-events-none">
                <div className="text-[10px] text-[var(--gold)] uppercase tracking-[0.3em] mb-1">SCORE</div>
                <div className="text-3xl font-display text-white">{score}</div>
              </div>
            </>
          )}
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Privacy Guardian"
        instructions={[
          { title: "Defend the Core", desc: "Incoming red trackers are attempting to breach your data core. You must deflect them to maintain system integrity." },
          { title: "Rotate the Shield", desc: "Your shield follows your cursor. Move your mouse around the core to position the shield in the path of incoming threats." },
          { title: "Capture Powerups", desc: "Green data packets occasionally appear. These are beneficial packets—let them through or deflect them for bonus points." },
          { title: "Monitor Integrity", desc: "Each breach reduces your 'Integrity' level. If it hits zero, a total data breach occurs and the system shuts down." }
        ]}
      />
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center justify-center md:justify-start gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default DataShieldGame;
